import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../layouts/Header';
import SideBar from '../../layouts/SideBar';
import { MdDelete, MdEdit, MdSearch } from 'react-icons/md';
import { toast } from 'react-toastify';
import { BASE_URL } from '../../api';

const API_BASE = `${BASE_URL}/api/routemasters`;

const Routepage = () => {
  const [routes, setRoutes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // States for Search and Pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const navigate = useNavigate();
  const token = localStorage.getItem('token') || '';

  const fetchRoutes = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(API_BASE, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error('Failed to fetch routes');
      const result = await response.json();
      if (result.data && Array.isArray(result.data)) {
        setRoutes(result.data);
      }
    } catch (err) {
      toast.error('Error fetching routes');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRoutes();
  }, []);

  /* ------------------------------------------------------------------ */
  /* SEARCH & PAGINATION LOGIC                                         */
  /* ------------------------------------------------------------------ */

  const filteredRoutes = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return routes.filter((r) =>
      (r.originid?.areaname || '').toLowerCase().includes(term) ||
      (r.destinationid?.areaname || '').toLowerCase().includes(term) ||
      (r.vehiclenumberText || r.vehiclenumber || '').toLowerCase().includes(term) ||
      (r.drivername || '').toLowerCase().includes(term)
    );
  }, [routes, searchTerm]);

  const totalPages = Math.ceil(filteredRoutes.length / pageSize);
  const paginatedRoutes = filteredRoutes.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleEdit = (route) => navigate('/add-route', { state: { route } });

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this route?')) return;
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE}/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        setRoutes((prev) => prev.filter((r) => r._id !== id));
        toast.success('Route deleted successfully');
      }
    } catch (err) {
      toast.error('Delete failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <div className="w-full">
        {/* Header Section */}
        <h2 className="text-2xl font-bold mb-6 text-gray-800">Route Master</h2>

        <div className="bg-white rounded-xl shadow p-6">
          {/* Toolbar */}
          <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
            <div className="relative w-full md:w-80">
              <MdSearch className="absolute left-3 top-2.5 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search origin, destination, vehicle..."
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full focus:ring-2 focus:ring-blue-500 outline-none transition"
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </div>

            <button
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium"
              onClick={() => navigate('/add-route')}
            >
              Add New Route
            </button>
          </div>

          {/* Table Section - Blue Header Design */}
          <div className="overflow-x-auto">
            <table className="w-full table-auto border-collapse">
              <thead className="bg-blue-600 text-white">
                <tr>
                  <th className="p-3 text-left">S.No</th>
                  <th className="p-3 text-left">Origin</th>
                  <th className="p-3 text-left">Destination</th>
                  <th className="p-3 text-left">Date</th>
                  <th className="p-3 text-left">Mode</th>
                  <th className="p-3 text-left">Driver</th>
                  <th className="p-3 text-left">Vehicle No</th>
                  <th className="p-3 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr><td colSpan={8} className="text-center py-10">Loading...</td></tr>
                ) : paginatedRoutes.length > 0 ? (
                  paginatedRoutes.map((route, idx) => {
                    // S.No Logic: Fixes search indexing
                    const sNo = searchTerm
                      ? routes.findIndex(r => r._id === route._id) + 1
                      : (currentPage - 1) * pageSize + idx + 1;

                    return (
                      <tr key={route._id} className="border-b hover:bg-gray-50 transition">
                        <td className="p-3 text-gray-600 font-medium">{sNo}</td>
                        <td className="p-3 font-semibold text-gray-800">{route.originid?.areaname || 'N/A'}</td>
                        <td className="p-3 font-semibold text-gray-800">{route.destinationid?.areaname || 'N/A'}</td>
                        <td className="p-3">{new Date(route.routedate).toLocaleDateString('en-GB')}</td>
                        <td className="p-3">
                          <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${route.modeid === 1 ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                            {route.modeid === 1 ? 'AIR' : route.modeid === 2 ? 'SURFACE' : 'SHIP'}
                          </span>
                        </td>
                        <td className="p-3">{route.drivername || '-'}</td>
                        <td className="p-3 font-mono">{route.vehiclenumberText || route.vehiclenumber || '-'}</td>
                        <td className="p-3 text-center">
                          <div className="flex justify-center gap-3">
                            <button onClick={() => handleEdit(route)} className="text-blue-600 hover:text-blue-800">
                              <MdEdit size={20} />
                            </button>
                            <button onClick={() => handleDelete(route._id)} className="text-red-600 hover:text-red-800">
                              <MdDelete size={20} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr><td colSpan={8} className="text-center py-10 text-gray-400 font-medium">No matching routes found</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls - Matching Design */}
          {totalPages > 1 && (
            <div className="flex justify-between items-center mt-6">
              <div className="text-sm text-gray-600">
                Showing <span className="font-semibold">{Math.min(filteredRoutes.length, (currentPage - 1) * pageSize + 1)}</span> to{' '}
                <span className="font-semibold">{Math.min(filteredRoutes.length, currentPage * pageSize)}</span> of{' '}
                <span className="font-semibold">{filteredRoutes.length}</span> entries
              </div>

              <div className="flex items-center gap-3">
                <select
                  value={pageSize}
                  onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                  className="border rounded-md px-2 py-1 text-sm outline-none focus:ring-1 focus:ring-blue-500"
                >
                  {[5, 10, 20, 50].map(size => <option key={size} value={size}>Show {size}</option>)}
                </select>

                <div className="flex gap-2">
                  <button
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(p => p - 1)}
                    className={`px-4 py-2 rounded-md bg-gray-200 transition ${currentPage === 1 ? 'text-gray-400 cursor-not-allowed' : 'hover:bg-gray-300 text-gray-700'}`}
                  >
                    Previous
                  </button>
                  <button
                    disabled={currentPage === totalPages || totalPages === 0}
                    onClick={() => setCurrentPage(p => p + 1)}
                    className={`px-4 py-2 rounded-md bg-gray-200 transition ${currentPage === totalPages || totalPages === 0 ? 'text-gray-400 cursor-not-allowed' : 'hover:bg-gray-300 text-gray-700'}`}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Routepage;